import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowRight, User, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const UserProfileForm = () => {
  const { user, refreshUser } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await api.get('/questions?category=user');
      setQuestions(response.data);
      
      // Check if user has already completed profile - load existing answers
      if (user?.has_completed_profile) {
        setIsEditMode(true);
        // Fetch existing answers
        try {
          const userRes = await api.get(`/users/my-answers?category=user`);
          if (userRes.data && userRes.data.answers) {
            const existingAnswers = {};
            userRes.data.answers.forEach(ans => {
              existingAnswers[ans.question_id] = ans.answer_value;
            });
            setAnswers(existingAnswers);
          }
        } catch (e) {
          // No existing answers, that's fine
        }
      }
    } catch (error) {
      toast.error('Failed to load profile questions');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/users/submit-answers', { answers, question_type: 'user' });
      toast.success(isEditMode ? 'Profile updated successfully!' : 'Profile information saved!');
      // Refresh user data
      await refreshUser();
      // Navigate to dashboard (skip redundant onboarding steps since account type is selected during registration)
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Failed to submit profile');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (question) => {
    const baseClass = "w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900";

    switch (question.field_type) {
      case 'textarea':
        return (
          <textarea
            data-testid={`question-${question.id}`}
            rows={4}
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="Enter your answer..."
          />
        );
      
      case 'select':
        return (
          <select
            data-testid={`question-${question.id}`}
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
          >
            <option value="">Select an option</option>
            {question.options?.map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );
      
      case 'phone':
        return (
          <input
            data-testid={`question-${question.id}`}
            type="tel"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="+1 (555) 000-0000"
          />
        );
      
      case 'email':
        return (
          <input
            data-testid={`question-${question.id}`}
            type="email"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="your@email.com"
          />
        );
      
      default:
        return (
          <input
            data-testid={`question-${question.id}`}
            type="text"
            value={answers[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            className={baseClass}
            placeholder="Enter your answer..."
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl border border-slate-200 p-10"
        >
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-emerald-900" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-slate-800 text-center mb-2">
            {isEditMode ? 'Edit Your Profile' : 'Complete Your Profile'}
          </h1>
          <p className="text-center text-slate-600 mb-8">
            {isEditMode ? 'Update your information below' : 'Please provide your basic information to continue'}
          </p>

          {isEditMode && (
            <div className="mb-6">
              <Link
                to="/admin/dashboard"
                className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {questions.length > 0 && questions.map((question) => (
              <div key={question.id}>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {question.question_text}
                </label>
                {renderField(question)}
              </div>
            ))}

            <div className={questions.length > 0 ? "pt-6 border-t border-slate-200" : ""}>
              <button
                type="submit"
                disabled={submitting}
                data-testid="profile-submit-button"
                className="w-full bg-emerald-900 hover:bg-emerald-800 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-4 px-8 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 group"
              >
                <span>{submitting ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Continue')}</span>
                {!submitting && <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default UserProfileForm;
